import type { DemoDoc, Step } from '@mado/schema';
import { resolveAssetUrl } from './media';
import { WebMixer, type Clip, type Mixer } from './mixer';
import type { Sfx, StartSource, StopReason } from './watch';

/**
 * Watch-mode audio: narration, a music bed, and a small fixed set of sound
 * effects (`Theme.audio` in packages/schema). Every rule here is a product
 * promise, not a preference:
 *
 *  - Audio only ever plays while watch mode is actively running. Ordinary
 *    click-through never creates an audio context or element, never touches
 *    the network for a clip, and this module does nothing until
 *    `notifyWatchStart` is called by the host (apps/player/src/runtime.ts) —
 *    which only happens from `Watch.start()`.
 *  - An autoplaying watch mode (no user gesture) starts muted, always — not
 *    "muted unless the browser would allow sound", muted regardless. The
 *    first *explicit* press of the sound control is what may ever unmute it,
 *    and that preference then holds for the rest of this page load (an
 *    in-memory flag, never persisted — CLAUDE.md rule 3). The viewer's volume
 *    is the same kind of in-memory value.
 *  - The voice never changes level. Every clip is normalised to the same
 *    loudness at generation (services/pipeline `audio/loudness.ts`) and plays
 *    at a fixed gain; only the music moves. It ducks when a clip actually
 *    starts making sound (not when it was asked to — a clip that is still
 *    loading would otherwise have the bed swell back mid-sentence) and comes
 *    back up when the clip ends.
 *  - The music bed is a seamless loop and always comes back: every start
 *    ramps its bus up to the resting level again, whatever a pause, a
 *    take-over, the end card or a hidden tab left it at.
 *  - A take-over or the end card fades everything out on the same beat as
 *    the visual stop.
 *
 * Levels match the MP4 export (services/pipeline `export/mix.ts`): the bed at
 * full gain (it is normalised 10 LU under the voice) ducked to 0.45.
 */

const SFX_BASE = '/p/sfx';
const SFX_FILES: Record<Sfx, string> = {
  press: 'press.mp3',
  whoosh: 'whoosh.mp3',
  type: 'type.mp3',
};

/** Kept low: a sound effect is a hint, not a soundtrack. */
const SFX_VOLUME = 0.35;
/** The music bed's resting level. Must match the MP4 mix (gain 1). */
export const MUSIC_VOLUME = 1;
/** Under narration. Must match `MUSIC_DUCK_GAIN` in services/pipeline `export/mix.ts`. */
export const DUCK_FACTOR = 0.45;
/** How quickly the bed dips when a voice starts, and comes back after. */
export const DUCK_ATTACK_MS = 250;
export const DUCK_RELEASE_MS = 700;
/** The bed fading in on (re)start. */
export const MUSIC_IN_MS = 400;
/** A take-over, a pause, or the player going offscreen — quick, so the sound
 *  stops with the visual on (about) the same frame. */
export const TAKEOVER_FADE_MS = 200;
/** Reaching the end card is not a take-over — a longer, deliberate fade. */
export const MUSIC_END_FADE_MS = 1200;
/** A clip that has not started making sound this long after it was asked
 *  for (a dead network, a blocked context) is given up on, so watch mode
 *  never waits on it forever. */
export const NARRATION_START_GRACE_MS = 4000;
/** After a clip starts, how far past its known duration `ended` may be late
 *  before it is treated as finished anyway. */
const NARRATION_END_GRACE_MS = 600;

export interface AudioHost {
  /** `sound_toggle` is always a human action — see packages/schema `ingest.ts`. */
  emit(type: 'sound_toggle', meta: { on: boolean }): void;
}

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : Number.isFinite(v) ? v : 1);

/**
 * `theme.audio` did not exist before narration/music/sfx shipped (CLAUDE.md
 * "the player has no zod… a document missing a field added later crashes
 * it"): a document published before then has no `audio` key on its `theme`
 * at all, forever — publish-time defaulting never runs again on an already-
 * published document. Every read below goes through this fallback rather
 * than `doc.theme.audio` directly, so an old document is silent (today's
 * behaviour) instead of throwing in the `Player` constructor, which runs
 * this eagerly for every mount, not just when Watch mode starts.
 */
const NO_AUDIO: NonNullable<DemoDoc['theme']['audio']> = {
  narration: false,
  music: false,
  musicStyle: 'minimal',
  musicAsset: null,
  sfx: false,
};

export class WatchAudio {
  /** Whether this demo has anything to play at all — the chrome sound
   *  control only exists when this is true. */
  readonly enabled: boolean;
  private muted = false;
  /** The viewer has explicitly pressed the sound control this session — once
   *  true, autoplay no longer gets to force muted back on. */
  private everToggled = false;
  private volume = 1;

  private mix: Mixer | null = null;

  private music: Clip | null = null;
  /** The bed should be audible (watch mode running, not muted). */
  private musicOn = false;
  /** Where the bed was when it last stopped — a resume carries on from there. */
  private musicPos = 0;
  private musicStop: ReturnType<typeof setTimeout> | null = null;
  private ducked = false;

  private voice: { clip: Clip; done: (at: number) => void } | null = null;
  private voiceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly doc: DemoDoc,
    private readonly host: AudioHost,
    private readonly assetBase: string | null | undefined,
    /** Whether watch mode is playing right now — read at call time, not
     *  captured at construction (this module is built before `Watch` is). */
    private readonly playing: () => boolean,
    /** Tests pass a fake; production gets WebAudio with an element fallback. */
    private readonly makeMixer: () => Mixer = () => new WebMixer(),
  ) {
    const a = doc.theme.audio ?? NO_AUDIO;
    this.enabled = a.narration || a.music || a.sfx;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  /** The viewer's volume, 0–1 (independent of mute). */
  getVolume(): number {
    return this.volume;
  }

  setVolume(v: number): void {
    this.volume = clamp01(v);
    this.mix?.ramp('master', this.volume, 60);
  }

  /**
   * The viewer pressed the sound control. Returns the new "sound is on"
   * state so the caller can reflect it on the chrome control without a
   * second round trip through `isMuted`.
   */
  toggle(): boolean {
    this.everToggled = true;
    this.muted = !this.muted;
    this.host.emit('sound_toggle', { on: !this.muted });
    if (this.muted) this.hardStop();
    else {
      // This press is the gesture iOS needs to let the context make sound.
      this.mixer().unlock();
      if (this.playing()) this.startMusic();
    }
    return !this.muted;
  }

  /** Watch mode has just started (or resumed). */
  notifyWatchStart(source: StartSource): void {
    // Autoplay never gets sound for free, no matter what the browser's own
    // autoplay-with-sound policy would allow — only an explicit press does,
    // and only once, ever, for the rest of this page load.
    if (source === 'autoplay' && !this.everToggled) this.muted = true;
    if (this.muted || !this.enabled) return;
    // Watch / the intro button: still inside the viewer's press.
    this.mixer().unlock();
    this.startMusic();
  }

  /** Watch mode has just stopped, for any reason. */
  notifyWatchStop(reason: StopReason): void {
    this.fadeOutAndStop(reason === 'end' ? MUSIC_END_FADE_MS : TAKEOVER_FADE_MS, reason === 'end');
  }

  /**
   * The narration duration (ms) a step will actually play in watch mode, or
   * `null` when there is nothing to play — narration is off, the demo has no
   * audio at all, sound is muted, the step has none, or its clip has not
   * been generated. Pure: no side effect, safe to call just to size a dwell.
   */
  narrationMs(step: Step): number | null {
    if (!this.enabled || this.muted || !this.doc.theme.audio?.narration) return null;
    const id = step.narration?.audio;
    if (!id) return null;
    const asset = this.doc.audioAssets?.[id];
    return asset ? asset.durationMs : null;
  }

  private narrationUrl(step: Step | undefined): string | null {
    const id = step?.narration?.audio;
    const asset = id ? this.doc.audioAssets?.[id] : undefined;
    return asset ? resolveAssetUrl(asset.src, this.assetBase) : null;
  }

  /**
   * Begin playing the step's narration. Resolves (with `performance.now()`)
   * once the clip has finished — or failed, been given up on, or been
   * stopped — so watch mode can hold the step until the sentence is over,
   * however late the clip started. `null` when there is nothing to play.
   * `offsetMs`: start that far into the clip (a seek); null when the clip is over by then.
   */
  playNarration(step: Step, offsetMs = 0): Promise<number> | null {
    const full = this.narrationMs(step);
    const url = full == null ? null : this.narrationUrl(step);
    if (full == null || !url || offsetMs >= full) return null;
    const ms = full - offsetMs;
    this.stopVoice();
    const m = this.mixer();
    return new Promise<number>((done) => {
      const entry = {
        clip: m.play('voice', url, {
          ...(offsetMs > 0 ? { offset: offsetMs / 1000 } : {}),
          onStart: () => {
            if (this.voice !== entry) return;
            this.duck(true);
            this.armVoiceTimer(ms + NARRATION_END_GRACE_MS);
          },
          onEnd: () => {
            if (this.voice === entry) this.stopVoice();
          },
        }),
        done,
      };
      this.voice = entry;
      this.armVoiceTimer(NARRATION_START_GRACE_MS + ms);
      this.prefetchAfter(step);
    });
  }

  /**
   * A short local sound effect — a press, a screen-transition whoosh, a
   * typing tick (a small fixed set the player fetches lazily from a
   * well-known same-origin path, never part of the document or its
   * `audioAssets`). Never throws, never awaited.
   */
  playSfx(name: Sfx): void {
    if (!this.enabled || this.muted || !this.doc.theme.audio?.sfx) return;
    this.mixer().play('sfx', resolveAssetUrl(`${SFX_BASE}/${SFX_FILES[name]}`, this.assetBase));
  }

  /** The player itself is going away. */
  destroy(): void {
    this.hardStop();
    this.mix?.destroy();
    this.mix = null;
  }

  private mixer(): Mixer {
    if (!this.mix) {
      const m = this.makeMixer();
      m.ramp('master', this.volume, 0);
      m.ramp('voice', 1, 0);
      m.ramp('music', 0, 0);
      m.ramp('sfx', SFX_VOLUME, 0);
      this.mix = m;
    }
    return this.mix;
  }

  /** Decode the clips the next step or two will want, so they start on time. */
  private prefetchAfter(step: Step): void {
    const m = this.mix;
    if (!m) return;
    const steps = this.doc.steps;
    const byId = (id: string | null | undefined) =>
      id ? steps.find((s) => s.id === id) : undefined;
    const i = steps.indexOf(step);
    const next = [
      byId(step.next),
      ...step.hotspots.map((h) => byId(h.next ?? null)),
      i >= 0 ? steps[i + 1] : undefined,
    ];
    const seen = new Set<string>();
    for (const s of next) {
      const url = this.narrationUrl(s);
      if (url && !seen.has(url) && seen.size < 2) {
        seen.add(url);
        m.preload(url);
      }
    }
  }

  private armVoiceTimer(ms: number): void {
    if (this.voiceTimer) clearTimeout(this.voiceTimer);
    this.voiceTimer = setTimeout(() => {
      this.voiceTimer = null;
      this.stopVoice();
    }, ms);
  }

  /** End the current clip (if any) and let the step move on. */
  private stopVoice(): void {
    if (this.voiceTimer) clearTimeout(this.voiceTimer);
    this.voiceTimer = null;
    const v = this.voice;
    if (!v) return;
    this.voice = null;
    v.clip.stop();
    this.duck(false);
    v.done(performance.now());
  }

  private musicLevel(): number {
    return this.ducked ? MUSIC_VOLUME * DUCK_FACTOR : MUSIC_VOLUME;
  }

  private duck(on: boolean): void {
    if (this.ducked === on) return;
    this.ducked = on;
    if (this.musicOn && this.mix) {
      this.mix.ramp('music', this.musicLevel(), on ? DUCK_ATTACK_MS : DUCK_RELEASE_MS);
    }
  }

  private startMusic(): void {
    if (!this.enabled || this.muted || !this.doc.theme.audio?.music) return;
    const id = this.doc.theme.audio?.musicAsset;
    const asset = id ? this.doc.audioAssets?.[id] : undefined;
    if (!asset) return;
    const m = this.mixer();
    if (this.musicStop) clearTimeout(this.musicStop);
    this.musicStop = null;
    this.musicOn = true;
    this.music ??= m.play('music', resolveAssetUrl(asset.src, this.assetBase), {
      loop: true,
      offset: this.musicPos,
    });
    // Always: whatever the last fade left the bus at, the bed comes back.
    m.ramp('music', this.musicLevel(), MUSIC_IN_MS);
  }

  /**
   * Narration stops at once (a half-spoken sentence over a take-over reads
   * as a glitch, not as restraint); the music bed ramps down over `ms`, then
   * stops. `rewind`: the end card — the next start begins the bed afresh.
   */
  private fadeOutAndStop(ms: number, rewind: boolean): void {
    this.stopVoice();
    this.musicOn = false;
    if (!this.music || !this.mix) return;
    this.mix.ramp('music', 0, ms);
    if (this.musicStop) clearTimeout(this.musicStop);
    this.musicStop = setTimeout(() => {
      this.musicStop = null;
      this.stopMusic(rewind);
    }, ms + 30);
  }

  private stopMusic(rewind: boolean): void {
    const clip = this.music;
    this.music = null;
    if (!clip) return;
    this.musicPos = rewind ? 0 : clip.position();
    clip.stop();
  }

  /** Immediate stop, no fade — a mute press, or the player tearing down. */
  private hardStop(): void {
    this.stopVoice();
    this.musicOn = false;
    if (this.musicStop) clearTimeout(this.musicStop);
    this.musicStop = null;
    this.stopMusic(false);
    this.mix?.ramp('music', 0, 0);
  }
}
